import { create } from "zustand";
import { getOrganizations, getUser, updateUser } from "@/services/user.client";
import { UserState } from "@/types/user";
import { toast } from "sonner";
import { DEFAULT_ERROR_TITLE } from "@/lib/constants";

export const useUserStore = create<UserState>()((set) => ({
  user: null,
  organizations: [],
  isLoading: false,
  error: null,
  hydrated: true,

  fetchUser: async (forceRefresh = false) => {
    try {
      set({ isLoading: true, error: null });

      const user = await getUser(forceRefresh);

      if (!user) throw new Error("Unable to fetch user");

      const organizations = await getOrganizations(user.organizations);

      set({
        user,
        organizations,
        isLoading: false,
      });
    } catch (error: any) {
      set({
        error: String(error.message) || "Failed to fetch user",
        isLoading: false,
      });

      throw new Error(error);
    }
  },

  updateUser: async (data) => {
    try {
      set({ isLoading: true, error: null });

      const updatedUser = await updateUser(data);

      if (!updatedUser) {
        set({
          error: "Failed to update user: No user data returned",
          isLoading: false,
        });
        return;
      }

      const organizations = await getOrganizations(updatedUser.organizations);

      set({
        user: updatedUser,
        organizations,
        isLoading: false,
      });
    } catch (error: any) {
      toast.error(DEFAULT_ERROR_TITLE, {
        description: "Failed to update user. Please try again later.",
      });

      set({
        error: String(error.message) || "Failed to update user",
        isLoading: false,
      });

      throw new Error(error);
    }
  },

  setUser: (user) => {
    set({ user });
  },

  setOrganizations: (organizations) => {
    set({ organizations });
  },

  setHydrated: (hydrated) => {
    set({ hydrated });
  },
}));
